import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import {
  EMPTY,
  Observable,
  catchError,
  concatMap,
  debounceTime,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { BucketService } from '../../../services/bucket.service';
import { ChatService } from '../../../services/chat.service';
import { DataSourcesService } from '../../../services/data-sources.service';
import {
  Tutor,
  TutorService,
  TutorSettingItem,
} from '../../../services/tutor.service';
import {
  ONBOARDING_TOUR_MAX_AUTOSTARTS_MAX,
  ONBOARDING_TOUR_MAX_AUTOSTARTS_MIN,
  ONBOARDING_TOUR_MAX_AUTOSTARTS_VARIABLE,
  ONBOARDING_TOUR_TOPICS,
  ONBOARDING_TOUR_TOPICS_VARIABLE,
  OnboardingTourTopic,
  availableOnboardingTopics,
  canSaveOnboardingTopics,
  clampOnboardingMaxAutostarts,
  mergeOnboardingTopics,
  savedOnboardingTopics,
  usesTwinWidgetTabs,
  visibleOnboardingTopicSelection,
} from './onboarding-tour-topics';

@Component({
  selector: 'app-assistant-settings-form',
  templateUrl: './assistant-settings-form.component.html',
  styleUrl: './assistant-settings-form.component.scss',
})
export class AssistantSettingsFormComponent implements OnChanges {
  @Input() data?: Tutor;

  form;

  shouldUpdate = true;

  /** The bot's `onboarding_tour_enabled` template setting (migration 104).
   *  Undefined while loading or on templates that don't carry it —
   *  the toggle row only renders once it's present. */
  onboardingSetting?: TutorSettingItem;
  private settingsLoadedForId?: string;

  /** Tour scope below the switch (`onboarding_tour_topics` /
   *  `onboarding_tour_max_autostarts`). Undefined on templates that don't
   *  carry them — the rows only render once present. */
  onboardingTopicsSetting?: TutorSettingItem;
  onboardingMaxAutostartsSetting?: TutorSettingItem;
  /** Topics this agent can explain — the selectable options, in canonical
   *  order. Assigned once per load (never rebuilt per change detection),
   *  empty until the Twin link and the template list are resolved so the
   *  list doesn't grow after the first paint. */
  availableOnboardingTopics: OnboardingTourTopic[] = [];
  /** Every topic is listed; those this agent can't explain (feature off,
   *  no linked Twin) stay visible but disabled, with the reason. */
  readonly allOnboardingTopics = ONBOARDING_TOUR_TOPICS;
  readonly onboardingMaxAutostartsMin = ONBOARDING_TOUR_MAX_AUTOSTARTS_MIN;
  readonly onboardingMaxAutostartsMax = ONBOARDING_TOUR_MAX_AUTOSTARTS_MAX;
  /** Template settings, not bots columns — saved through their own
   *  handlers, outside `form` and its auto-save. */
  readonly onboardingTopicsControl = new FormControl<string[]>([], {
    nonNullable: true,
    validators: Validators.required,
  });
  readonly onboardingMaxAutostartsControl = new FormControl<number | null>(
    null,
    [
      Validators.required,
      Validators.min(ONBOARDING_TOUR_MAX_AUTOSTARTS_MIN),
      Validators.max(ONBOARDING_TOUR_MAX_AUTOSTARTS_MAX),
      Validators.pattern(/^\d+$/),
    ]
  );

  constructor(
    protected fb: FormBuilder,
    protected filesService: BucketService,
    protected tutorService: TutorService,
    protected dataSourcesService: DataSourcesService,
    protected chatService: ChatService
  ) {
    // ai_hint moved to AI configuration (Sicherheit/Guardrails section)
    // and is no longer editable from the General tab — exclude it from
    // the form so updateTutor() doesn't silently reset it to false.
    this.form = fb.group({
      id: this.fb.control(''),
      name: this.fb.control('', Validators.required),
      description: this.fb.control(''),
      template_id: this.fb.control('', Validators.required),
      data_source_ids: this.fb.control([]),
      widget_setting_id: this.fb.control(''),
      visual_name: this.fb.control(''),
      welcome_message: this.fb.control('', Validators.required),
    });

    this.form.controls.data_source_ids.setValue([]);
    this.dataSourcesService.listDataSources().subscribe();
    this.chatService.listChatSettings().subscribe();
    this.tutorService.listTutorTemplates().subscribe();

    // disable template_id always
    this.form.controls.template_id.disable();

    this.onboardingTopicsControl.valueChanges
      .pipe(
        tap((selected) => {
          // Empty → red right away (required); nothing is saved.
          if (
            !canSaveOnboardingTopics(selected, this.availableOnboardingTopics)
          ) {
            this.onboardingTopicsControl.markAsTouched();
          }
        }),
        // One PATCH per pause in clicking, never one per click — and one
        // at a time, in order (each carries the full list: last one wins).
        debounceTime(350),
        concatMap(() => this.saveOnboardingTopics())
      )
      .subscribe();
    this.onboardingMaxAutostartsControl.valueChanges
      .pipe(
        tap(() => {
          // Out of range / not a whole number → red while typing.
          if (this.onboardingMaxAutostartsControl.invalid) {
            this.onboardingMaxAutostartsControl.markAsTouched();
          }
        }),
        debounceTime(400)
      )
      .subscribe(() => this.saveOnboardingMaxAutostarts());
  }

  /** True when the active bot is an alpha_dozent. The General page hides
   *  chat-styling for these because the dozent never runs as a standalone
   *  chat — its widget look is dictated by the linked tutor. */
  get isDozentBot(): boolean {
    if (!this.data?.template_id) return false;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === this.data!.template_id
    );
    return !!template && template.name === 'alpha_dozent';
  }

  isOnboardingTopicAvailable(topic: OnboardingTourTopic): boolean {
    return this.availableOnboardingTopics.includes(topic);
  }

  get onboardingTourOn(): boolean {
    const s = this.onboardingSetting;
    if (!s) return true;
    const v = s.value ?? s.default_value;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  onToggleOnboardingTour(checked: boolean) {
    if (!this.data?.id || !this.onboardingSetting) return;
    this.onboardingSetting = { ...this.onboardingSetting, value: checked };
    this.tutorService
      .updateTutorSettings(this.data.id, {
        [String(this.onboardingSetting.setting_id)]: checked,
      })
      .subscribe();
  }

  /** Saves the dropdown's current selection (read when this save's turn
   *  comes, so a queued save never sends an outdated list): the visible
   *  topics come from the dropdown, hidden ones keep their saved state. An
   *  empty selection is not saved — the field is red (required) and the
   *  last saved value stays. On error the last saved value comes back. */
  private saveOnboardingTopics(): Observable<unknown> {
    const botId = this.data?.id;
    const setting = this.onboardingTopicsSetting;
    const available = this.availableOnboardingTopics;
    const selected = this.onboardingTopicsControl.value;
    if (!botId || !setting || !canSaveOnboardingTopics(selected, available)) {
      return EMPTY;
    }
    const value = mergeOnboardingTopics(selected, setting, available);
    const saved = savedOnboardingTopics(setting);
    if (
      value.length === saved.length &&
      value.every((topic, i) => topic === saved[i])
    ) {
      return EMPTY;
    }
    this.onboardingTopicsSetting = { ...setting, value };
    return this.tutorService
      .updateTutorSettings(botId, { [String(setting.setting_id)]: value })
      .pipe(
        catchError(() => {
          // updateTutorSettings clears its loading flag only on success.
          this.tutorService.loading$.next(false);
          if (this.data?.id === botId) {
            this.onboardingTopicsSetting = setting;
            this.applyOnboardingTopicsSelection(setting);
          }
          return EMPTY;
        })
      );
  }

  /** Puts the saved selection into the dropdown without triggering a save.
   *  An empty one (no overlap with the available topics, or a stored `[]`)
   *  is marked touched so the global required paint (rule 5) flags it. */
  private applyOnboardingTopicsSelection(setting: TutorSettingItem) {
    const control = this.onboardingTopicsControl;
    control.reset(
      visibleOnboardingTopicSelection(setting, this.availableOnboardingTopics),
      { emitEvent: false }
    );
    if (!control.value.length) control.markAsTouched();
  }

  private saveOnboardingMaxAutostarts() {
    const botId = this.data?.id;
    const setting = this.onboardingMaxAutostartsSetting;
    const control = this.onboardingMaxAutostartsControl;
    if (!botId || !setting || control.invalid) return;
    const value = clampOnboardingMaxAutostarts(control.value);
    const saved = clampOnboardingMaxAutostarts(
      setting.value ?? setting.default_value
    );
    if (value === saved) return;
    this.onboardingMaxAutostartsSetting = { ...setting, value };
    this.tutorService
      .updateTutorSettings(botId, { [String(setting.setting_id)]: value })
      .pipe(
        catchError(() => {
          this.tutorService.loading$.next(false);
          if (this.data?.id === botId) {
            this.onboardingMaxAutostartsSetting = setting;
            control.reset(saved, { emitEvent: false });
          }
          return EMPTY;
        })
      )
      .subscribe();
  }

  /** Leaving the field invalid: a typed number is clamped to 1..10 (and
   *  rounded) and saved; an empty field gets the saved count back. */
  onOnboardingMaxAutostartsBlur() {
    const setting = this.onboardingMaxAutostartsSetting;
    const control = this.onboardingMaxAutostartsControl;
    if (!setting || control.valid) return;
    const typed = control.value;
    const isNumber = typeof typed === 'number' && Number.isFinite(typed);
    control.setValue(
      clampOnboardingMaxAutostarts(
        isNumber ? typed : setting.value ?? setting.default_value
      ),
      { emitEvent: false }
    );
    if (isNumber) this.saveOnboardingMaxAutostarts();
  }

  private loadOnboardingSetting() {
    const botId = this.data?.id;
    if (!botId || this.settingsLoadedForId === botId) return;
    this.settingsLoadedForId = botId;
    this.onboardingTopicsSetting = undefined;
    this.onboardingMaxAutostartsSetting = undefined;
    this.availableOnboardingTopics = [];
    this.tutorService.fetchTutorSettings(botId).subscribe((settings: any) => {
      this.onboardingSetting = (settings as TutorSettingItem[]).find(
        (s) => s.variable_name === 'onboarding_tour_enabled'
      );
      if (this.data?.id === botId) {
        this.loadOnboardingTourScope(botId, settings as TutorSettingItem[]);
      }
    });
  }

  /** Topics + autostart count. Which topics the agent can explain depends
   *  on its own feature settings, on an attached Alpha Twin (lecturer
   *  step; the Twin's tabs while `use_twin_widget_tabs` is on) and on the
   *  template name (`tutor_basic` exercise mode) — so it waits for the
   *  template list as well. */
  private loadOnboardingTourScope(botId: string, settings: TutorSettingItem[]) {
    this.onboardingMaxAutostartsSetting = settings.find(
      (s) => s.variable_name === ONBOARDING_TOUR_MAX_AUTOSTARTS_VARIABLE
    );
    if (this.onboardingMaxAutostartsSetting) {
      const s = this.onboardingMaxAutostartsSetting;
      this.onboardingMaxAutostartsControl.reset(
        clampOnboardingMaxAutostarts(s.value ?? s.default_value),
        { emitEvent: false }
      );
    }

    const topicsSetting = settings.find(
      (s) => s.variable_name === ONBOARDING_TOUR_TOPICS_VARIABLE
    );
    if (!topicsSetting) return;

    const twin$ = this.tutorService.getAttachedDozent(botId).pipe(
      catchError(() => of(null)),
      switchMap((attached) => {
        const twinId = attached?.dozent_bot_id;
        const none: TutorSettingItem[] = [];
        if (!twinId) return of({ linkedTwin: false, twinSettings: none });
        if (!usesTwinWidgetTabs(settings)) {
          return of({ linkedTwin: true, twinSettings: none });
        }
        return this.tutorService.fetchTutorSettings(twinId).pipe(
          catchError(() => of([] as TutorSettingItem[])),
          map((twinSettings) => ({ linkedTwin: true, twinSettings }))
        );
      })
    );
    const templates$ = this.tutorService.tutorTemplates$.pipe(
      filter((templates) => templates.length > 0),
      take(1)
    );

    forkJoin({ twin: twin$, templates: templates$ }).subscribe(
      ({ twin: { linkedTwin, twinSettings }, templates }) => {
        if (this.data?.id !== botId) return;
        const templateName = templates.find(
          (t) => t.id === this.data?.template_id
        )?.name;
        this.availableOnboardingTopics = availableOnboardingTopics({
          settings,
          templateName,
          linkedTwin,
          twinSettings,
        });
        this.onboardingTopicsSetting = topicsSetting;
        this.applyOnboardingTopicsSelection(topicsSetting);
      }
    );
  }

  ngOnInit() {
    if (this.data) {
      this.form.patchValue(this.data as any);
      this.loadOnboardingSetting();
    }

    this.form.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      console.log('debounceTime', this.shouldUpdate);
      if (this.shouldUpdate === false) {
        this.shouldUpdate = true;
        return;
      }

      this.tutorService
        .updateTutor({
          ...this.data,
          ...(this.form.value as Tutor),
        })
        .subscribe(() => {});
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('ngOnChanges');
    if (changes['data'].currentValue.id !== this.form.value.id) {
      if (this.form.value.id) {
        this.shouldUpdate = false;
      }
      this.form.patchValue(changes['data'].currentValue as any);
    }
    this.loadOnboardingSetting();
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
  }

  showInputError(name: string) {
    return this.form.get(name)?.touched && this.form.get(name)?.invalid;
  }
}
